// Sample report sections, for previews of report types an activity doesn't have: the Reports
// page (SocialReportsPage.svelte) and the local template gallery (Gallery.svelte).
import type { ReportData } from './types'

// The stock backgrounds the sample ride offers when the caller doesn't pass the current ones.
const SAMPLE_STOCK = ['https://www.xertonline.com/assets/images/Hero-Carousel-1-bg.jpg', 'https://www.xertonline.com/assets/images/group_ride_dolomites.jpg']

// A plausible ride for users with no activity to preview yet.
function sampleActivity(stock: string[]): ReportData {
  const n = 300
  const t = Array.from({ length: n }, (_, i) => i * 20)
  const power = t.map((_, i) => Math.round(190 + 60 * Math.sin(i / 9) + 40 * Math.sin(i / 3.1) + (i % 37 === 0 ? 260 : 0)))
  const hr = t.map((_, i) => Math.round(132 + 14 * Math.sin((i - 4) / 9) + 6 * Math.sin((i - 2) / 3.1) + i * 0.04))
  const alt = t.map((_, i) => Math.round(120 + 70 * Math.sin(i / 40) + 25 * Math.sin(i / 11)))
  const lat: number[] = []
  const lng: number[] = []
  const dist: number[] = []
  for (let i = 0; i < 200; i++) {
    const a = (i / 199) * Math.PI * 2
    lat.push(45.5 + 0.06 * Math.sin(a) + 0.012 * Math.sin(a * 5))
    lng.push(-73.6 + 0.09 * Math.cos(a) + 0.01 * Math.cos(a * 3))
    dist.push((i / 199) * 62.4)
  }
  return {
    version: 2,
    units: 'metric',
    activity: {
      path: 'sample', name: 'Morning Ride', type: 'Cycling', sport: 'cycling', virtual: false, date: new Date().toISOString().slice(0, 19),
      duration: 7860, moving_time: 7620, distance: 62.4, elevation_gain: 780, avg_power: 205, max_power: 812, xep: 238, avg_hr: 146, max_hr: 178,
      avg_speed: 29.5, max_speed: 61.2, avg_cadence: 88, calories: 1640, xss: 172, xlss: 128, xhss: 31, xpss: 13, difficulty: 118,
      difficulty_stars: 4, difficulty_label: 'Hard', focus: 'Breakaway Specialist', focus_duration: 412, specificity: 'Mixed', carbs: 212, fat: 41,
      power: {
        avg: { w: 205, wkg: 2.83 }, mov: { w: 212, wkg: 2.92 }, pedal: { w: 231, wkg: 3.19 }, xep: { w: 238, wkg: 3.28 }, rp: null,
      },
      recovery: { low: 0.6, high: 1.7, peak: 3.2 },
    },
    athlete: { name: 'You', first_name: 'You', avatar: null },
    signature: { tp: 247, hie: 19.1, pp: 905, ltp: 183 },
    breakthrough: null, xfai: null, workout: null, group: null,
    series: { t, power, mpa: null, hr, alt, target: null, zones: { ltp: 180, ftp: 247 }, bt_index: null },
    route: { lat, lng, dist },
    backgrounds: { stock, streetview: null },
    types: ['activity'],
    recommended_type: 'activity',
    auto_type: null,
  }
}

/**
 * `data` with sample sections filled in for every report type it lacks. Without `data`, a sample
 * ride stands in, offering `stock` (the admin-set stock backgrounds) as its backgrounds.
 */
export function withSamples(base: ReportData | null, stock?: string[]): ReportData {
  const data = base ?? sampleActivity(stock?.length ? stock : SAMPLE_STOCK)
  const sample: ReportData = {
    ...data,
    activity: {
      ...data.activity,
      // Relative Power only comes with a group ride; the sample group report shows one.
      power: data.group || !data.activity.power ? data.activity.power : { ...data.activity.power, rp: data.activity.power.rp ?? { w: 226, wkg: null } },
      recovery: data.activity.recovery ?? { low: 0.6, high: 1.7, peak: 3.2 },
    },
    breakthrough: data.breakthrough ?? {
      medal: 2, level: 'silver', time: data.series?.t[Math.floor((data.series?.t.length ?? 0) * 0.6)] ?? null,
      before: { tp: 241, hie: 18.2, pp: 887, ltp: 180 }, after: { tp: 247, hie: 19.1, pp: 905, ltp: 183 }, delta: { tp: 6, hie: 0.9, pp: 18 },
    },
    xfai: data.xfai ?? {
      plan_name: 'Gran Fondo Stelvio', focus: { name: 'GC Specialist', description: '8 Minute W/kg' }, day: 71, days_total: 120, phase: 'Build',
      target: { xss: 150, xlss: 110, xhss: 32, xpss: 8 }, completed: { xss: 162, xlss: 121, xhss: 31, xpss: 10 },
      compliance: { xss: 'Good', xlss: 'Good', xhss: 'Good', xpss: 'Warn' }, day_kind: 'high',
      status: { label: 'Productive', form: 'Tired', rating: 3.5, color: '#F5A623' }, recovery: { low: 0.4, high: 2.1 },
    },
    workout: data.workout ?? {
      name: 'SMART Over-Unders 3x12', description: null, has_targets: true,
      program: { type: 'targetEvent', day: 71, days_total: 120, phase: 'Build' },
    },
    group: data.group ?? {
      ride: {
        riders_total: 9, pulling_power: 248, squad: 'Toronto Dawn Patrol',
        riders: [
          { name: 'Sam Keller', avatar: null, blurred: false, medal: null },
          { name: 'Priya Natarajan', avatar: null, blurred: false, medal: 3 },
          { name: null, avatar: null, blurred: true, medal: null },
          { name: 'Jo Martins', avatar: null, blurred: false, medal: null },
          { name: 'Luca Bianchi', avatar: null, blurred: false, medal: null },
        ],
      },
      session: {
        name: 'Tuesday Night Threshold', workout_name: 'Threshold Builder 2x20', participants: 12, rank: 2,
        awards: { xss: 'Priya Natarajan', difficulty: 'You', wkg: 'Sam Keller' },
        leaderboard: [
          { rank: 1, name: 'Priya Natarajan', avatar: null, blurred: false, is_me: false, xss: 171, difficulty: 132, wkg: 3.9, medal: 3 },
          { rank: 2, name: data.athlete.name, avatar: data.athlete.avatar, blurred: false, is_me: true, xss: 165, difficulty: 141, wkg: 3.6, medal: null },
          { rank: 3, name: 'Sam Keller', avatar: null, blurred: false, is_me: false, xss: 158, difficulty: 120, wkg: 4.1, medal: null },
          { rank: 4, name: null, avatar: null, blurred: true, is_me: false, xss: 149, difficulty: 117, wkg: 3.2, medal: null },
          { rank: 5, name: 'Jo Martins', avatar: null, blurred: false, is_me: false, xss: 140, difficulty: 109, wkg: 3.0, medal: null },
        ],
      },
      highest: ['difficulty', 'power.rp.w'],
    },
    types: ['breakthrough', 'xfai', 'workout', 'group', 'activity'],
  }
  if (sample.series && !sample.series.target) {
    // Sample interval targets: alternating over/under blocks.
    const n = sample.series.t.length
    sample.series = { ...sample.series, target: sample.series.t.map((_, i) => (i < n * 0.1 || i > n * 0.9 ? 140 : Math.floor(i / (n / 16)) % 2 ? 180 : 300)) }
  }
  return sample
}
